/**
 * «¿Qué es este contacto?»: el desplegable que reemplaza al interruptor.
 *
 * Es el widget `tipo_de_contacto` del campo `is_company` de `base.partner`, y es
 * la respuesta al pedido textual del dueño: «en la zona de agregar contacto, en
 * vez de que diga "es una empresa", que tenga la opción de poner si es o no un
 * consorcio».
 *
 * Qué arregla
 * -----------
 * «Es una empresa» es vocabulario de ERP genérico: no le dice nada a quien carga
 * los contactos de una empresa de trámites cuyos dos segmentos son los
 * administraciones de consorcios y los consorcios. Y el tipo de cliente hasta hoy
 * **se deducía y no se podía declarar**: el sistema lo calcula mirando si el
 * contacto tiene ficha de administracion, si es el `partner` de un edificio o si
 * es dueño de una unidad, así que decir «esta es una administracion» al cargarlo
 * era imposible — había que crear la ficha después, y la acción que lo hace ni
 * siquiera aparece en el formulario (es `TARGET_SELECTION`, y `ActionBar`
 * esconde las de selección adentro de una ficha: hay que volver al listado y
 * tildar la fila).
 *
 * La decisión que sostiene todo: NO se guarda ninguna etiqueta
 * ------------------------------------------------------------
 * Este control no escribe un tipo. Escribe dos clases de cosa, y las dos son
 * duras:
 *
 * 1. `is_company` (y `is_customer`): hechos del contacto, que ya se guardaban.
 * 2. **La fila que hace verdadera la afirmación**: la ficha de la administracion, o
 *    el vínculo del contacto con un edificio. Las crea llamando a las acciones
 *    de servidor que YA existen, por la misma API que el resto del sistema.
 *
 * `client_kind` sigue siendo un caché deducido por señal, que nadie escribe a
 * mano. Por eso lo que este control muestra no puede contradecir a la realidad:
 * lee lo que el sistema dedujo, no una intención que alguien tipeó. Un contacto
 * que dice «consorcio» y no tiene edificio **no puede existir**, porque
 * «consorcio» no es una etiqueta que se guarda: es un vínculo a un edificio que
 * ya está cargado.
 *
 * Ni una palabra del negocio está escrita acá
 * -------------------------------------------
 * Las opciones, sus ayudas, qué escribe cada una, qué acción dispara y por qué
 * una está bloqueada vienen todas en `meta.options`, que arma el backend
 * (`base/schema.py` las dos genéricas, `property_mgmt/schema.py` las del
 * oficio). Sin el vertical instalado llegan sólo «Una persona» y «Una empresa»;
 * sin ninguna opción, este widget se corre y deja el interruptor de siempre.
 *
 * El consorcio SÍ se crea, y por qué eso no contradice nada
 * ----------------------------------------------------------
 * Acá decía «**no crea el consorcio**», con este argumento: un consorcio es una
 * empresa contable propia —plan de cuentas, diarios, ejercicio— más el
 * edificio, y fabricarlo desde el formulario de un contacto sería inventar la
 * jurisdicción y la moneda y parir media empresa contable que después se niega
 * a liquidar. El argumento vale entero y no se tocó: **lo que rompe es
 * inventar esos datos, no crear el consorcio.**
 *
 * Lo que cambió es que ya no hay que inventarlos. El dueño se topó con el otro
 * lado del problema —cargó un contacto como consorcio, el sistema se lo guardó
 * y le avisó, y el consorcio no apareció en Consorcios— y pidió que eso no
 * pueda volver a pasar: o se crea, o se dice claramente qué falta. Así que hay
 * dos opciones de consorcio: la de siempre, que ata el contacto a un edificio
 * que YA existe, y una nueva que lo da de alta entero **pidiendo** la
 * administración, la jurisdicción y la moneda, con la misma acción de servidor
 * que usa «Nuevo consorcio» desde la administración.
 *
 * Y este widget sigue sin saber una sola palabra del oficio: los controles de
 * esa opción son los `ActionParam` que declara **la propia acción**, leídos de
 * su metadata y dibujados con `ActionParamControl`, que es el mismo control que
 * usa el diálogo de la botonera. Ni una etiqueta duplicada, ni un nombre de
 * campo del negocio escrito en este archivo.
 *
 * CAMBIAR EL TIPO DE UN CONTACTO QUE YA ES ALGO
 * ---------------------------------------------
 * Acá decía «**no deshace nada**»: si el contacto ya era administración,
 * administrador o consorcio, el desplegable lo mostraba y venía `disabled`. El
 * dueño lo probó sobre su cartera y lo llamó, con razón, «no funciona bien»: los
 * 3.770 contactos que ya tenía cargados son todos administraciones, así que el
 * control estaba muerto en el 98,6 % de su base.
 *
 * Ahora **sí** se puede cambiar, y lo que hace que se pueda no está en este
 * archivo: es `services/conversion_de_tipo` del backend, que **retira la ficha
 * vieja o se niega diciendo qué la está reteniendo, cuántos son y en qué
 * pantalla verlos**. Sin ese servicio, destrabar el `<select>` habría cambiado
 * un «no me deja» por un error de integridad de la base en inglés —los dos
 * `partner` son `OneToOne PROTECT`— o, peor, por consorcios sin administrador
 * asignado en silencio, porque `Building.administrador` es `SET_NULL` y la base
 * no se niega sola. Este widget no decide nada de eso: ofrece, llama y muestra
 * el parte.
 *
 * La única que sigue sin poder cambiarse desde acá es la ficha comercial de un
 * consorcio, y el motivo está en el `porque` que manda el backend: retirarla
 * dejaría al edificio sin con qué facturarle, y eso se desata mirando el
 * edificio.
 *
 * Y la casilla de persona/empresa sigue a mano igual que siempre, porque ese
 * dato es **ortogonal** al tipo y hay casos reales de las dos formas: una
 * administracion persona física y un propietario sociedad anónima.
 *
 * CUÁNDO CORRE LA ACCIÓN: depende de si el contacto ya existe
 * ----------------------------------------------------------
 * En un ALTA se anota para después del guardado (`alGuardar`), porque hasta que
 * el guardado no vuelve no hay id sobre el que correrla.
 *
 * Sobre un contacto que YA está guardado corre **en el momento**, con un botón
 * propio. El porqué está entero al lado del botón; en corto: los datos que pide
 * la acción no son campos del contacto, así que completarlos no ensucia el
 * formulario, y «Guardar» contestaba «No hay cambios para guardar» y la ficha no
 * se creaba nunca. La anotación se mantiene igual además del botón —apretar
 * «Guardar» es el reflejo natural y ese camino también tiene que funcionar—, y
 * `hecho` impide que los dos corran dos veces.
 */

import { useEffect, useRef, useState } from 'react';

import { BooleanField } from '@/components/fields/BooleanField';
import { controlClass } from '@/components/fields/controls';
import { ReadonlyOutput } from '@/components/fields/FieldDisplay';
import { isMany2One } from '@/components/fields/format';
import { RelationPicker } from '@/components/fields/RelationPicker';
import type { FieldInputProps } from '@/components/fields/types';
import { useMetaQuery } from '@/components/shell/queries';
import { notify } from '@/components/shell/toast';
import { ApiError, api } from '@/lib/api';
import type { ActionParam, Domain, FieldValue, JsonValue, RecordData } from '@/lib/types';
import { ParamsDeLaAccion } from '@/views/ActionDialog';
import { useGuardadoDeLaFicha } from '@/views/MotivoBloqueo';
import { useInvalidateData, useRecordsQuery } from '@/views/queries';

// ---------------------------------------------------------------------------
// Lo que manda el backend
// ---------------------------------------------------------------------------
/** El dato que la opción «Un consorcio» necesita además de elegirse. */
interface ParamDeLaOpcion {
  nombre: string;
  etiqueta: string;
  relacion: string;
  dominio?: Domain;
  ayuda?: string;
  /** Qué decir cuando no hay ni un registro para elegir. */
  vacio?: string;
  /** Qué decir cuando la persona eligió la opción y no eligió el registro. */
  falta?: string;
}

/** Una opción del desplegable, tal como la declara el módulo que la aporta. */
interface OpcionDeTipo {
  valor: string;
  etiqueta: string;
  ayuda?: string;
  /** Qué escribe en el propio campo. */
  is_company?: boolean;
  /** Campos hermanos que además completa. */
  is_customer?: boolean;
  /** Acción de servidor que crea la fila real. Sin esto la opción sólo escribe. */
  accion?: string;
  /** `'seleccion'` va al endpoint de colección; `'registro'`, al del registro. */
  accion_alcance?: 'seleccion' | 'registro';
  /** Valor de `client_kind` que el sistema deduce cuando la fila existe. */
  client_kind?: string;
  /** El CUIT es condición para que la fila se pueda crear. */
  pide_cuit?: boolean;
  falta_cuit?: string;
  /** Por qué, una vez hecho, esto no se cambia desde acá. */
  porque?: string;
  param?: ParamDeLaOpcion;
  /**
   * `true` si lo que esta opción necesita son los `ActionParam` que declara su
   * PROPIA acción.
   *
   * Es el camino para las opciones que piden más de un dato —la que da de alta
   * un consorcio pide cinco—, y evita el problema que traía copiarlos acá: las
   * mismas cinco etiquetas escritas en dos archivos, de los cuales el que se
   * olvida de actualizarse es siempre la copia. Se leen de la metadata del
   * modelo, que ya viaja para saber qué acciones puede correr el usuario, y se
   * dibujan con `ActionParamControl`, el mismo control del diálogo.
   *
   * Convive con `param` en vez de reemplazarlo: `param` es un `<select>` de
   * candidatos que sabe decir «no hay ninguno para elegir», y ese aviso es
   * justamente lo que le explica a la persona por qué la lista está vacía. El
   * buscador del `many2one` no tiene forma de decirlo.
   */
  params_de_la_accion?: boolean;
  /**
   * Con qué campo hermano del formulario se prellena cada parámetro:
   * `{nombre del parámetro: nombre del campo}`.
   *
   * Sirve para no pedir dos veces el mismo dato. El valor NO se copia al
   * elegir: se lee en vivo del formulario mientras nadie haya tocado el
   * control, así que escribir el nombre del contacto después de elegir la
   * opción también lo actualiza. En cuanto la persona lo edita, manda lo que
   * escribió.
   */
  prellenar?: Record<string, string>;
  /** Qué decir cuando la persona guardó y faltaba completar algún parámetro. */
  falta?: string;
}

/** Un tipo que el sistema deduce y que NO se puede declarar. Ver el docstring. */
interface TipoDeducido {
  client_kind: string;
  etiqueta: string;
  porque?: string;
}

/**
 * Cómo se llama, ADENTRO DE LA FICHA, cada parámetro que no se llama igual.
 *
 * El panel muestra lo que ya está cargado leyendo la ficha y buscando cada
 * parámetro por su nombre. Casi todos coinciden —`celular_numero`, `email`,
 * `caba_calle`— y ésos no están acá. Los que no coinciden son pocos y hasta hoy
 * salían **en blanco**: un «Nombre del consorcio» vacío en un consorcio que
 * obviamente tiene nombre no se lee como «no lo pudimos traducir», se lee como
 * «este dato falta», y alguien lo escribe de nuevo.
 *
 * LOS `many2one` TAMBIEN («¿De qué administración?», «¿Qué administrador lo
 * atiende?»). La ficha los devuelve como `{id, display_name}`, que es justo lo
 * que el buscador necesita para dibujar la pastilla; antes se descartaban por no
 * ser texto y el panel mostraba un buscador vacío sobre un consorcio que SÍ
 * tenía administración —«no se guarda a qué administración pertenece», dijo el
 * dueño, y se guardaba: no se mostraba—. Al servidor viaja sólo el id.
 */
const ALIAS_DE_LA_FICHA: Record<string, Record<string, string>> = {
  'property.manager': { matricula: 'registration_number' },
  'property.building': {
    nombre: 'name',
    administracion: 'manager',
    jurisdiccion: 'jurisdiction',
    direccion: 'street',
    localidad: 'city',
  },
};

/**
 * Lee las opciones con desconfianza: cualquier forma inesperada se ignora y el
 * control degrada al interruptor de siempre, que es lo que hace que un `options`
 * mal escrito sea un campo feo y no un ERP en blanco.
 */
function leerOpciones(bruto: JsonValue | undefined): OpcionDeTipo[] {
  if (!Array.isArray(bruto)) return [];
  return (bruto as unknown[]).filter(
    (opcion): opcion is OpcionDeTipo =>
      typeof opcion === 'object' &&
      opcion !== null &&
      typeof (opcion as { valor?: unknown }).valor === 'string' &&
      typeof (opcion as { etiqueta?: unknown }).etiqueta === 'string',
  );
}

function leerDeducidas(bruto: JsonValue | undefined): TipoDeducido[] {
  if (!Array.isArray(bruto)) return [];
  return (bruto as unknown[]).filter(
    (tipo): tipo is TipoDeducido =>
      typeof tipo === 'object' &&
      tipo !== null &&
      typeof (tipo as { client_kind?: unknown }).client_kind === 'string' &&
      typeof (tipo as { etiqueta?: unknown }).etiqueta === 'string',
  );
}

/** Texto de un campo hermano, o `''`. */
function texto(valor: FieldValue | undefined): string {
  return typeof valor === 'string' ? valor.trim() : '';
}

// ---------------------------------------------------------------------------
// El widget
// ---------------------------------------------------------------------------
export function TipoDeContactoField({
  id,
  meta,
  value,
  onChange,
  readonly,
  invalid,
  describedBy,
  context,
  siblings,
  ...resto
}: FieldInputProps) {
  /**
   * Lo que la persona eligió y todavía no es cierto en la base.
   *
   * Sólo hace falta para las opciones que además crean una fila: hasta que el
   * guardado vuelva, `client_kind` sigue vacío y sin esto el desplegable
   * "rebotaría" a «Una empresa» delante de quien acaba de elegir otra cosa.
   */
  const [elegida, setElegida] = useState<string | null>(null);
  /** El edificio elegido para la opción que lo pide. */
  const [registroDelParam, setRegistroDelParam] = useState<number | null>(null);
  /** Su nombre, para la pastilla del buscador (el desplegable lo tiene en sus opciones). */
  const [nombreDelParam, setNombreDelParam] = useState<string | null>(null);
  /**
   * Lo que la persona ESCRIBIÓ en los parámetros de la acción.
   *
   * Sólo lo escrito: un parámetro que no está acá adentro se lee del campo
   * hermano que declara `prellenar`, en vivo. Guardar además lo prellenado
   * congelaría el nombre que se tipeó DESPUÉS de elegir la opción, que es el
   * orden natural en un alta.
   */
  const [valoresDeLaAccion, setValoresDeLaAccion] = useState<Record<string, JsonValue>>({});
  /** Qué parámetro quedó vacío en el último intento: se pinta en su control. */
  const [faltantes, setFaltantes] = useState<Record<string, string>>({});
  /** `true` mientras la acción está en vuelo. */
  const [corriendo, setCorriendo] = useState(false);
  /**
   * El parte del servidor cuando la acción YA se hizo, o `null` si falta.
   *
   * Existe por dos motivos y los dos son del defecto 2:
   *
   * 1. **Apaga la anotación de «al guardar».** Si la persona ya apretó el botón
   *    de acá abajo, apretar «Guardar» después no puede volver a correrla: sería
   *    un segundo aviso contando lo mismo, o peor, sobre un contacto que mientras
   *    tanto cambió.
   * 2. **Dice que se hizo sin depender de que la pantalla se refresque.** El
   *    contacto ya está guardado, así que lo que corre no es un guardado: si
   *    esto no estuviera, entre que la acción vuelve y que `client_kind` llega
   *    de nuevo del servidor la pantalla se ve idéntica a antes de apretar — que
   *    es exactamente lo que el dueño describió como «el sistema contesta que sí
   *    y no hace nada».
   */
  const [hecho, setHecho] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // COMPLETAR LA FICHA DE UN CONTACTO QUE YA ES DEL VERTICAL
  // -------------------------------------------------------------------------
  // El defecto, textual: «si lo guardas sin direcciones y claves y etc despues
  // no podes modificar el contacto de manera correcta».
  //
  // La causa era una asimetria: al ALTA se le podian pasar los veintitres datos
  // de la ficha desde este mismo formulario, pero las acciones de alta son
  // idempotentes HACIA EL NO-HACER —si la ficha ya existia contestaban «ya era
  // administrador, no se toco nada»—. Lo que no se cargaba en el primer intento
  // no se podia cargar nunca mas desde aca: habia que ir a otra pantalla, que es
  // justo la que quien carga una cartera no sabe que existe.
  //
  // Este panel corre `completar_ficha_del_vertical`, que ESCRIBE sobre la ficha
  // existente y deja intacto lo que se mande vacio.
  const [valoresDeLaFicha, setValoresDeLaFicha] = useState<Record<string, JsonValue>>({});
  const [guardandoFicha, setGuardandoFicha] = useState(false);
  const [fichaLista, setFichaLista] = useState(false);
  /**
   * Los campos hermanos que escribió ESTE control, para poder deshacerlos si la
   * persona cambia de idea sin pisar lo que ya estaba puesto de antes.
   */
  const escritoPorMi = useRef<{ is_customer?: boolean }>({});

  const guardado = useGuardadoDeLaFicha();
  const guardadoRef = useRef(guardado);
  guardadoRef.current = guardado;
  const invalidar = useInvalidateData();

  const opciones = leerOpciones(meta.options?.['opciones']);
  const deducidas = leerDeducidas(meta.options?.['deducidas']);
  const campoDeducido =
    typeof meta.options?.['campo_deducido'] === 'string'
      ? (meta.options['campo_deducido'] as string)
      : '';

  const valores = siblings?.values ?? {};
  const deducido = campoDeducido === '' ? '' : texto(valores[campoDeducido]);
  const cuit = texto(valores.cuit);

  /**
   * Las acciones que este usuario PUEDE ejecutar sobre contactos.
   *
   * La API ya poda las acciones por grupo, así que una opción cuya acción no
   * llegó es una que siempre iba a fallar. Es la misma decisión que `ActionBar`
   * ya toma con los botones: lo que el backend deshabilitó no se dibuja.
   */
  const metadatos = useMetaQuery(context.modelName);
  const acciones = metadatos.data?.actions ?? [];
  const permitidas = new Set(acciones.map((accion) => accion.name));

  /** La opción que corresponde a lo que el contacto ES hoy, según la base. */
  const porDeduccion =
    deducido === '' ? undefined : opciones.find((opcion) => opcion.client_kind === deducido);
  const deducidaNoElegible =
    deducido === '' || porDeduccion !== undefined
      ? undefined
      : deducidas.find((tipo) => tipo.client_kind === deducido);

  /** La opción base: la que se deduce de `is_company` cuando no hay nada más. */
  const porBooleano = opciones.find(
    (opcion) => opcion.accion === undefined && opcion.is_company === (value === true),
  );

  /** La opción que la persona eligió y todavía no es cierta en la base. */
  const opcionElegida = opciones.find((opcion) => opcion.valor === elegida);

  // Si la realidad alcanzó a lo elegido, se suelta la elección local: a partir de
  // ahí manda lo que dice la base, que es la única fuente que no puede mentir.
  //
  // Se compara por `client_kind` y no por `valor`, y ésa es la diferencia que
  // trajo la segunda opción de consorcio: las dos dejan al contacto siendo lo
  // mismo —la ficha comercial de un consorcio— por caminos distintos, así que
  // comparar los valores dejaría «Un consorcio que todavía no está cargado»
  // elegido para siempre sobre un consorcio que YA se creó.
  useEffect(() => {
    if (elegida === null) return;
    if (opcionElegida?.client_kind === undefined || opcionElegida.client_kind !== deducido) return;
    setElegida(null);
    setRegistroDelParam(null);
    setNombreDelParam(null);
    setValoresDeLaAccion({});
    setFaltantes({});
    setHecho(null);
  }, [elegida, opcionElegida, deducido]);

  /**
   * Lo que el contacto YA ES, según la base. Antes esto DESHABILITABA el
   * desplegable; ahora sólo decide qué texto se lee al lado.
   *
   * POR QUÉ SE DESTRABÓ (defecto 3). El pedido textual del dueño: «cuando
   * modificas un contacto existente para cambiar por ejemplo de administracion a
   * administrador no funciona bien». No funcionaba porque **no dejaba**: en
   * cuanto el contacto tenía su ficha, el `<select>` venía `disabled` con el
   * cartel «Mientras la tenga, esto no se cambia desde acá». Y como los 3.770
   * contactos de la cartera real son todos administraciones, eso era el 98,6 %
   * de la base con el desplegable muerto.
   *
   * LO QUE HACÍA FALTA ANTES DE SACARLO, y por eso no era una línea: convertir
   * no es reetiquetar, hay un BORRADO. Sin el servicio que retira la ficha vieja
   * —`services/conversion_de_tipo`, que cuenta lo que la retiene y se niega
   * nombrándolo— sacar el `disabled` habría cambiado un «no me deja» por un
   * error de integridad de la base en inglés, o peor, por consorcios sin
   * administrador asignado en silencio (`Building.administrador` es SET_NULL: la
   * base NO se niega sola). Ese servicio existe, así que esto se puede destrabar.
   */
  const yaEsAlgo = porDeduccion !== undefined && elegida === null;

  /**
   * Un PROPIETARIO no está bloqueado, y la diferencia importa: declararlo
   * administracion **agrega** una ficha, no saca la unidad que ya tiene, y de
   * hecho la precedencia del sistema ya contempla a la administracion que además
   * es propietario de una unidad del edificio que administra.
   *
   * Lo que sí se hace es no ofrecerle «Una persona» / «Una empresa»: elegirlas
   * no cambiaría lo que muestra —seguiría siendo propietario, que es cierto— y
   * un desplegable que rebota es peor que uno que no ofrece. Ese eje se corrige
   * con la casilla de más abajo, que sigue estando.
   */
  const soloDeclarables = deducidaNoElegible !== undefined && elegida === null;

  const actual =
    elegida ?? porDeduccion?.valor ?? (deducidaNoElegible !== undefined ? deducido : porBooleano?.valor ?? '');

  const opcionActual = opciones.find((opcion) => opcion.valor === actual);
  const paramPendiente = opcionActual?.param;
  const necesitaAccion = elegida !== null && opcionActual?.accion !== undefined;
  /**
   * La persona eligió algo DISTINTO de lo que el contacto ya es: esto es una
   * CONVERSIÓN y no un alta.
   *
   * Cambia dos textos y nada más —el del botón y el que dice qué va a pasar—,
   * pero son justo los dos que tienen que avisar que hay una ficha que se
   * retira. Quién decide si se puede es el backend, siempre.
   */
  const yaEraOtraCosa = necesitaAccion && deducido !== '';

  /**
   * La accion que completa la ficha, segun QUE es el contacto.
   *
   * Son TRES y no una porque los datos son otros: una administracion tiene
   * matricula, celular y accesos a MIBA; un administrador tiene lo mismo menos
   * la matricula, y ademas de que firma cuelga; un consorcio tiene CUIT,
   * jurisdiccion y direccion. El widget dibuja la que corresponda.
   *
   * CADA UNA PIDE EXACTAMENTE LO QUE PIDE SU ALTA, que es la regla que el dueño
   * dijo dos veces: «un contacto que ya tenes guardado si lo queres editar no te
   * aparecen todos los mismos campos que a la hora de agregar un contacto de
   * cero, entonces hay cosas que no podes editar ni borrar o agregar». Los
   * paneles y las altas salen de las mismas declaraciones en el backend, asi que
   * no pueden volver a separarse sin que se note en las dos pantallas.
   */
  const accionCompletar = acciones.find(
    (a) =>
      a.name ===
      (deducido === 'consorcio'
        ? 'completar_ficha_del_consorcio'
        : deducido === 'administracion'
          ? 'completar_ficha_de_la_administracion'
          : 'completar_ficha_del_vertical'),
  );
  /**
   * Se muestra el panel de completar cuando el contacto YA es del vertical y la
   * persona no esta en medio de una conversion.
   *
   * `deducido` es lo que el SISTEMA sabe que el contacto es —sale de
   * `client_kind`, que se recalcula por señal— y no lo que alguien eligio en el
   * desplegable: es el unico dato que no puede mentir.
   */
  const puedeCompletar =
    context.recordId !== null &&
    elegida === null &&
    (deducido === 'administracion' ||
      deducido === 'administrador' ||
      deducido === 'consorcio') &&
    accionCompletar !== undefined;

  // LOS DATOS QUE YA ESTAN CARGADOS SE MUESTRAN, que es la otra mitad del
  // pedido: «cuando entras a un administrador ya guardado te aparezcan todos los
  // datos, direcciones y demas». Sin esto el panel saldria en blanco y no habria
  // forma de saber que hay cargado sin ir a la otra pantalla.
  //
  // Se lee la ficha por dominio en vez de por la relacion inversa del contacto
  // porque el nombre de esa relacion es del vertical y este widget es generico.
  useEffect(() => {
    if (!puedeCompletar || fichaLista) return;
    const modelo =
      deducido === 'administracion'
        ? 'property.manager'
        : deducido === 'consorcio'
          ? 'property.building'
          : 'property.administrador';
    // EL CONSORCIO ES SU PROPIA EMPRESA, y por eso lleva una condición de más.
    //
    // El listado se acota SOLO a la empresa activa del conmutador, y el edificio
    // de un consorcio vive en la empresa de ese consorcio, no en la de Plena.
    // Sin esta línea la búsqueda devolvía cero filas SIEMPRE y el panel del
    // consorcio salía en blanco aunque tuviera todo cargado —«Nombre del
    // consorcio» vacío en un consorcio que se llama «Torre Panel»—.
    //
    // `company != null` no filtra nada —todo edificio tiene empresa—: está para
    // NOMBRAR a `company`, que es la manera declarada de pedir la vista
    // consolidada. Ver `_apply_company_scope` en `core/api.py`: «si el dominio
    // ya habla de company, no se pisa… es lo que deja pedir la vista
    // consolidada explícitamente». Lo que acota de verdad sigue firme: el
    // tenant y las empresas habilitadas del usuario.
    const dominio: Domain =
      deducido === 'consorcio'
        ? ([
            ['partner', '=', context.recordId as number],
            ['company', '!=', null],
          ] as Domain)
        : ([['partner', '=', context.recordId as number]] as Domain);
    let vigente = true;
    void api
      .searchRead(modelo, {
        domain: dominio,
        limit: 1,
      })
      .then((respuesta: { results?: RecordData[] }) => {
        if (!vigente) return;
        const fila = respuesta.results?.[0];
        if (fila !== undefined) {
          const previos: Record<string, JsonValue> = {};
          for (const param of accionCompletar?.params ?? []) {
            // EL PARAMETRO Y EL CAMPO NO SIEMPRE SE LLAMAN IGUAL. La mayoria si
            // —por eso el `?? param.name`—, pero unos pocos no, y son justo los
            // que quedaban en blanco: «Nombre del consorcio» vacio en un
            // consorcio que tiene nombre parece un dato que falta, y lo que
            // faltaba era la traduccion.
            const campo = ALIAS_DE_LA_FICHA[modelo]?.[param.name] ?? param.name;
            const valor = (fila as Record<string, unknown>)[campo];
            // Las claves NO vuelven del servidor para quien no esta en el grupo
            // habilitado: ahi el campo simplemente no viene y el control queda
            // vacio, que es lo correcto.
            if (typeof valor === 'string' && valor !== '') previos[param.name] = valor;
            else if (isMany2One(valor)) {
              previos[param.name] = { id: valor.id, display_name: valor.display_name };
            }
          }
          setValoresDeLaFicha(previos);
        }
        setFichaLista(true);
      })
      .catch(() => {
        // No se pudo leer: el panel se dibuja igual, vacio. Completar sobre
        // vacio no borra nada, asi que el peor caso es que no se vea lo que ya
        // habia — nunca que se pierda.
        if (vigente) setFichaLista(true);
      });
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeCompletar, fichaLista, deducido, context.recordId]);

  // -------------------------------------------------------------------------
  // Los datos que pide la acción de la opción, declarados por ella misma
  // -------------------------------------------------------------------------
  /**
   * Los `ActionParam` a dibujar, o vacío si esta opción no los usa.
   *
   * Salen de la metadata que ya se pidió para saber qué acciones puede correr
   * el usuario: no hay una consulta de más. Si la acción no llegó —porque este
   * usuario no la tiene—, la lista queda vacía y la opción tampoco se ofrece,
   * que es la misma poda de siempre.
   */
  const paramsDeclarados: ActionParam[] =
    opcionActual?.params_de_la_accion === true
      ? (acciones.find((accion) => accion.name === opcionActual.accion)?.params ?? []).filter(
          // El `param` VIEJO gana sobre el declarado que se llame igual, y no es
          // capricho: son el mismo dato pedido de dos maneras y dibujar las dos
          // sería pedirlo dos veces en la misma pantalla.
          //
          // El caso concreto es «Un administrador»: la acción declara
          // `administracion` como many2one obligatorio, y la opción además trae
          // el `<select>` de candidatos de siempre. Se queda el viejo porque
          // sabe decir algo que un buscador de relación no puede —«Todavía no
          // hay ninguna administración cargada. Cargala primero…»—, que es
          // justamente lo que le explica a la persona por qué la lista está
          // vacía. Sin este filtro, poner `params_de_la_accion` en esa opción
          // dibujaba dos controles para «¿De qué administración?».
          (param) => param.name !== opcionActual.param?.nombre,
        )
      : [];

  /**
   * Lo que hoy vale un parámetro: lo que la persona escribió, y si no tocó
   * nada, el campo hermano con el que se prellena. Ver `prellenar`.
   */
  const leerParam = (nombre: string): JsonValue => {
    if (nombre in valoresDeLaAccion) return valoresDeLaAccion[nombre] ?? null;
    const campo = opcionActual?.prellenar?.[nombre];
    if (campo === undefined) return null;
    const valorHermano = valores[campo];
    return typeof valorHermano === 'string' && valorHermano.trim() !== '' ? valorHermano : null;
  };

  /**
   * FRENA EL GUARDADO cuando falta un dato que la acción necesita.
   *
   * EL DEFECTO QUE ARREGLA, encontrado por el dueño probando el sistema: cargaba
   * un contacto, elegía «Un consorcio que todavía no está cargado», dejaba sin
   * completar la administración o la moneda, y apretaba Guardar. El contacto
   * quedaba creado, el consorcio NO, y lo único que lo decía era un aviso
   * flotante que se va solo. Después abría Consorcios y estaba vacío. Su frase
   * fue: «agrego un consorcio desde contactos y no me aparece».
   *
   * La causa era el ORDEN: la comprobación de los obligatorios vivía adentro de
   * `ejecutar`, que corre DESPUÉS del guardado (`alGuardar`). O sea que cuando
   * descubría que faltaba algo, el contacto ya estaba escrito.
   *
   * Acá se comprueba ANTES, interceptando el `submit` en fase de captura —el
   * mismo recurso que usa `useDirtyGuard` para los enlaces— y se cancela. Nada
   * se guarda: ni el contacto ni la ficha. El error queda pegado a cada campo
   * que falta y no en un globo que se desvanece.
   *
   * Media alta es peor que ninguna: un contacto suelto que dice ser un consorcio
   * y no está en Consorcios es un dato que miente, y nadie va a ir a buscarlo.
   */
  useEffect(() => {
    if (!necesitaAccion) return;
    if (paramsDeclarados.length === 0 && paramPendiente === undefined) return;

    const alEnviar = (evento: Event): void => {
      const vacios = paramsDeclarados.filter((param) => {
        if (!param.required) return false;
        const valor = leerParam(param.name);
        return valor === null || (typeof valor === 'string' && valor.trim() === '');
      });

      // EL `param` VIEJO TAMBIEN FRENA, y no lo hacía: éste es el desplegable
      // propio de la opción —«¿De qué administración?», «¿De qué edificio?»— que
      // el widget dibuja en vez del `ActionParam` que se llama igual.
      //
      // Sin esta mitad quedaba viva exactamente la falla que este freno existe
      // para matar, sólo que por la otra puerta: elegís «Un administrador», no
      // tocás el desplegable, apretás Crear, y el contacto se guarda mientras la
      // ficha de administrador NO se crea — el único aviso, un globo que se
      // desvanece diciendo «Falta «¿De qué administración?»». Medido en la
      // pantalla del archivo entregado.
      const faltaElPropio = paramPendiente !== undefined && registroDelParam === null;

      if (vacios.length === 0 && !faltaElPropio) return;

      evento.preventDefault();
      evento.stopPropagation();
      setFaltantes({
        ...Object.fromEntries(vacios.map((param) => [param.name, 'Este dato es obligatorio.'])),
        ...(faltaElPropio && paramPendiente !== undefined
          ? { [paramPendiente.nombre]: paramPendiente.falta ?? 'Este dato es obligatorio.' }
          : {}),
      });
      const nombres = [
        ...vacios.map((param) => `«${param.label}»`),
        ...(faltaElPropio && paramPendiente !== undefined ? [`«${paramPendiente.etiqueta}»`] : []),
      ];
      notify.error(
        `Falta completar ${nombres.join(', ')}`,
        'No se guardó nada todavía: completalo y volvé a guardar. Así no queda un ' +
          'contacto suelto sin su ficha.',
      );
    };

    document.addEventListener('submit', alEnviar, true);
    return () => {
      document.removeEventListener('submit', alEnviar, true);
    };
    // `leerParam` se rearma en cada render y lee estado fresco; lo que decide si
    // el freno tiene que existir es qué se eligió y qué pide esa acción.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [necesitaAccion, paramsDeclarados, paramPendiente, registroDelParam, valoresDeLaAccion, valores]);

  // -------------------------------------------------------------------------
  // Los edificios que todavía no tienen ficha comercial
  // -------------------------------------------------------------------------
  // Se piden sólo cuando la persona ELIGIÓ la opción que los necesita, y no
  // cuando el contacto ya es un consorcio: ahí el dato ya está y la consulta
  // sería una petición de más en cada ficha de las nueve que hay cargadas.
  //
  // CON BUSCADOR, NO CON DESPLEGABLE, cuando la opción no trae un `dominio`
  // propio. Era un `<select>` con las primeras 200 por nombre y nada para
  // escribir, y con las 3.766 administraciones de Plena cargadas el 95 % no se
  // podía elegir: «salen las primeras y no podés buscar otra». El buscador es el
  // mismo de todo many2one —pide al servidor lo que se tipea—. El desplegable
  // queda sólo para la opción con `dominio` (los edificios sin ficha comercial),
  // que son pocos y el buscador no sabe filtrar por dominio.
  //
  // Para el buscador esta consulta sólo dice si HAY alguno (`count`): es lo que
  // permite seguir mostrando «Todavía no hay ninguna administración cargada…».
  const conBuscador = (paramPendiente?.dominio ?? []).length === 0;
  const consultaParam = useRecordsQuery(
    paramPendiente?.relacion ?? '',
    {
      domain: paramPendiente?.dominio ?? [],
      limit: conBuscador ? 1 : 200,
      order: 'name',
      fields: ['name'],
    },
    necesitaAccion && paramPendiente !== undefined && !readonly,
  );
  const candidatos = consultaParam.data?.results ?? [];

  // -------------------------------------------------------------------------
  // Lo que hay que hacer DESPUÉS de que el contacto esté guardado
  // -------------------------------------------------------------------------
  /**
   * Ejecuta la acción sobre el contacto ya guardado.
   *
   * Va en un `ref` porque la corre el formulario cuando el guardado vuelve, y en
   * un alta eso pasa mientras esta pantalla se está reemplazando por la del
   * contacto recién creado: la función ya está en vuelo y sabe su id, pero el
   * componente puede estar desmontándose. Por eso no usa `useMutation` —cuyos
   * callbacks no corren si el componente se fue— sino la promesa pelada, el
   * toast global y el invalidador, que no dependen de que esto siga montado.
   */
  const ejecutar = useRef<(idContacto: number) => Promise<void>>(async () => {});
  ejecutar.current = async (idContacto: number) => {
    const opcion = opciones.find((o) => o.valor === elegida);
    if (opcion?.accion === undefined) return;

    const parametros: Record<string, JsonValue> = {};
    if (opcion.param !== undefined) {
      if (registroDelParam === null) {
        notify.warning(
          `Falta «${opcion.param.etiqueta}»`,
          opcion.param.falta ?? 'Elegí el registro y volvé a guardar.',
        );
        return;
      }
      parametros[opcion.param.nombre] = registroDelParam;
    }

    // Los parámetros declarados por la acción. Se comprueba ACÁ lo obligatorio
    // aunque el servidor lo vuelva a comprobar, y no es redundancia: sin esto,
    // guardar con un dato vacío deja un 400 en un aviso flotante que no dice
    // cuál de los cinco falta, y el registro que la acción iba a crear no
    // existe sin que nadie sepa por qué. Es exactamente el defecto que el dueño
    // encontró —«se guardó igual y me avisó», y el consorcio en ningún lado—.
    if (paramsDeclarados.length > 0) {
      const vacios = paramsDeclarados.filter((param) => {
        if (!param.required) return false;
        const valor = leerParam(param.name);
        return valor === null || (typeof valor === 'string' && valor.trim() === '');
      });
      if (vacios.length > 0) {
        setFaltantes(
          Object.fromEntries(vacios.map((param) => [param.name, 'Este dato es obligatorio.'])),
        );
        notify.warning(
          `Falta completar ${vacios.map((param) => `«${param.label}»`).join(', ')}`,
          opcion.falta ??
            'El contacto quedó guardado. Completá lo que falta y volvé a guardar.',
        );
        return;
      }
      setFaltantes({});
      for (const param of paramsDeclarados) {
        const valor = leerParam(param.name);
        // Un opcional vacío no se manda: el backend le pone su `default`, y
        // pisarlo con `null` sería mandarle nada encima. Mismo criterio que
        // `cleanParamValues` del diálogo.
        if (valor === null || (typeof valor === 'string' && valor.trim() === '')) continue;
        parametros[param.name] = valor;
      }
    }

    setCorriendo(true);
    try {
      const resultado =
        opcion.accion_alcance === 'seleccion'
          ? await api.callCollectionAction(context.modelName, opcion.accion, [idContacto], parametros)
          : await api.callAction(context.modelName, idContacto, opcion.accion, parametros);
      // El que cuenta qué pasó es el servidor: el parte viene redactado y dice
      // lo que se hizo, lo que ya estaba, **lo que se retiró** y lo que quedó
      // afuera con su motivo.
      notify.success(opcion.etiqueta, resultado.message ?? undefined);
      // Se anota que ya está hecho ANTES de invalidar: el toast se va solo en
      // unos segundos y la pantalla puede tardar en enterarse de que el contacto
      // cambió de tipo. Sin esta marca, esos segundos se ven exactamente igual
      // que si no hubiera pasado nada.
      setHecho(resultado.message ?? 'Listo.');
      await invalidar(context.modelName);
    } catch (error) {
      // El contacto QUEDÓ creado: no se puede decir «no se guardó», porque se
      // guardó. Lo que faltó es el paso de más, y se dice cuál y qué hacer.
      notify.error(
        'El contacto quedó guardado, pero faltó el último paso',
        error instanceof ApiError ? error.message : 'Reintentá desde la ficha del contacto.',
      );
    } finally {
      setCorriendo(false);
    }
  };

  /**
   * Anota la acción para después del guardado, y la desanota si la persona
   * cambia de idea. La firma de la dependencia es lo elegido y el registro
   * elegido: nada más mueve la anotación.
   */
  const firma = `${elegida ?? ''}|${registroDelParam ?? ''}`;
  useEffect(() => {
    if (!necesitaAccion) return;
    // Ya se hizo: apretar «Guardar» después no puede volver a correrla.
    if (hecho !== null) return;
    const alGuardar = guardadoRef.current?.alGuardar;
    if (alGuardar === undefined) return;
    return alGuardar((registro) => {
      void ejecutar.current(registro.id);
    });
    // `ejecutar` es un ref y `alGuardar` cambia de identidad en cada render del
    // formulario; lo que mueve la anotación es qué se eligió.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma, necesitaAccion, hecho]);

  // -------------------------------------------------------------------------
  // Degradaciones
  // -------------------------------------------------------------------------
  // Sin opciones declaradas —nadie las inyectó, o llegaron mal formadas— este
  // campo vuelve a ser lo que siempre fue: el booleano de `base`.
  if (opciones.length === 0) {
    return (
      <BooleanField
        id={id}
        meta={meta}
        value={value}
        onChange={onChange}
        readonly={readonly}
        invalid={invalid}
        context={context}
        {...(describedBy === undefined ? {} : { describedBy })}
        {...resto}
      />
    );
  }

  if (readonly) {
    const etiqueta = porDeduccion?.etiqueta ?? deducidaNoElegible?.etiqueta ?? porBooleano?.etiqueta ?? '';
    return (
      <ReadonlyOutput
        id={id}
        meta={{ ...meta, type: 'char' }}
        value={etiqueta}
        {...(describedBy === undefined ? {} : { describedBy })}
      />
    );
  }

  // -------------------------------------------------------------------------
  // El control
  // -------------------------------------------------------------------------
  const elegir = (valor: string): void => {
    const opcion = opciones.find((o) => o.valor === valor);
    if (opcion === undefined) return;

    setRegistroDelParam(null);
    setNombreDelParam(null);
    // Lo tipeado en los parámetros de la opción anterior se descarta: son datos
    // de OTRA acción. Lo prellenado no hay que rearmarlo, porque no se copia
    // nunca: se lee del formulario en cada render.
    setValoresDeLaAccion({});
    setFaltantes({});
    // Y el parte de la anterior también: cambiar de opción vuelve a dejar algo
    // por hacer, aunque lo de antes se haya hecho.
    setHecho(null);

    // Lo que la opción escribe en el propio campo. Siempre viaja por `onChange`,
    // que es la vía normal de cualquier control del formulario.
    if (typeof opcion.is_company === 'boolean') onChange(opcion.is_company);

    // Y lo que escribe en los hermanos. `is_customer` porque el motivo entero de
    // que estas dos fichas existan es poder venderles.
    //
    // **Sólo se deshace lo que escribió este widget**, que es la misma regla que
    // gobierna al selector de productos: si quien carga elige «administracion» y
    // después vuelve a «Una persona», la bandera de cliente que puso el widget
    // se saca; una que ya estaba puesta —porque el contacto era cliente desde
    // antes— no se toca.
    if (siblings !== undefined) {
      if (opcion.is_customer === true && valores.is_customer !== true) {
        escritoPorMi.current.is_customer = true;
        siblings.setValues({ is_customer: true });
      } else if (opcion.is_customer !== true && escritoPorMi.current.is_customer === true) {
        escritoPorMi.current.is_customer = false;
        siblings.setValues({ is_customer: false });
      }
    }

    // Sólo las opciones que además crean una fila necesitan recordarse: las
    // otras dos YA son ciertas apenas se escribe el booleano.
    setElegida(opcion.accion === undefined ? null : opcion.valor);
  };

  const visibles = opciones.filter((opcion) => {
    // Lo que el backend no le habilitó a este usuario no se dibuja: es la misma
    // decisión que `ActionBar` ya toma con los botones. Nunca se esconde, eso
    // sí, la opción que describe lo que el contacto YA es: sería un desplegable
    // que no puede mostrar su propio valor.
    if (opcion.valor === actual) return true;
    if (opcion.accion === undefined) return !soloDeclarables;
    return permitidas.has(opcion.accion);
  });

  const faltaCuit = opcionActual?.pide_cuit === true && cuit === '';

  return (
    <div className="space-y-2">
      <select
        id={id}
        className={controlClass(invalid, 'pr-8')}
        value={actual}
        // Sólo mientras la acción está en vuelo. Lo que el contacto YA es NO
        // lo traba más: ver `yaEsAlgo`.
        disabled={corriendo}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(evento) => {
          elegir(evento.target.value);
        }}
      >
        {/* La línea de un tipo que el sistema dedujo y que no se puede declarar
            —«Propietario de una unidad»— existe sólo para poder mostrar el valor
            que el contacto tiene. Ver el docstring. */}
        {deducidaNoElegible !== undefined && elegida === null && (
          <option value={deducido}>{deducidaNoElegible.etiqueta}</option>
        )}
        {visibles.map((opcion) => (
          <option key={opcion.valor} value={opcion.valor}>
            {opcion.etiqueta}
          </option>
        ))}
      </select>

      {/* Qué significa lo que está elegido, en criollo.
          NO se muestra cuando el contacto YA es eso: la ayuda de la opción está
          escrita en imperativo —«al guardar se le crea la ficha», «elegí abajo
          de qué edificio es»— porque describe lo que hace ELEGIRLA. Sobre un
          consorcio que ya está vinculado, esa instrucción es una tarea que ya no
          hay que hacer, y la línea que corresponde es la de abajo, que cuenta lo
          que pasó. */}
      {!yaEsAlgo && (opcionActual?.ayuda ?? '') !== '' && (
        <p className="text-xs text-fg-faint">{opcionActual?.ayuda}</p>
      )}

      {/* De dónde salió lo que muestra, y por qué no se cambia desde acá. Nada
          se apaga en silencio: el desplegable de una administracion está
          deshabilitado y esta línea es la que dice el motivo. */}
      {deducido !== '' && elegida === null && (
        <p role="status" className="text-xs text-fg-muted">
          {porDeduccion?.porque ??
            deducidaNoElegible?.porque ??
            'Lo deduce el sistema de lo que hay cargado.'}
        </p>
      )}

      {/* El dato ortogonal, disponible siempre que el tipo lo tape.
          `is_company` y el tipo de cliente son cosas distintas y hay casos reales
          de las cuatro combinaciones: una administracion persona física y un
          propietario sociedad anónima están cargados hoy. Sin esta casilla, el
          desplegable aplanaría los dos ejes en uno. */}
      {(deducido !== '' || necesitaAccion) && (
        <label className="flex items-start gap-2 text-xs text-fg-muted">
          <input
            type="checkbox"
            checked={value === true}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-line accent-[rgb(var(--accent))]"
            onChange={(evento) => {
              onChange(evento.target.checked);
            }}
          />
          <span>En los papeles es una empresa (razón social con CUIT), no una persona.</span>
        </label>
      )}

      {/* El dato que la opción necesita además de elegirse: de qué edificio. */}
      {necesitaAccion && paramPendiente !== undefined && (
        <div className="rounded-md border border-dashed border-line bg-app-subtle px-3 py-2.5">
          <label
            htmlFor={`${id}-param`}
            className="mb-1 block text-xs font-medium text-fg-muted"
          >
            {paramPendiente.etiqueta}
          </label>
          {consultaParam.isPending ? (
            <p className="text-xs text-fg-faint">Buscando…</p>
          ) : candidatos.length === 0 ? (
            <p className="text-xs text-fg-muted">{paramPendiente.vacio ?? 'No hay ninguno para elegir.'}</p>
          ) : conBuscador && registroDelParam !== null ? (
            <div className="flex items-center gap-1.5">
              <span className="min-w-0 flex-1 truncate rounded-md border border-line bg-app px-3 py-2 text-sm">
                {nombreDelParam ?? `#${registroDelParam}`}
              </span>
              <button
                type="button"
                className="shrink-0 rounded-md border border-line px-2 py-2 text-xs text-fg-muted hover:text-fg"
                onClick={() => {
                  setRegistroDelParam(null);
                  setNombreDelParam(null);
                }}
              >
                Cambiar
              </button>
            </div>
          ) : conBuscador ? (
            <RelationPicker
              id={`${id}-param`}
              relation={paramPendiente.relacion}
              required
              invalid={faltantes[paramPendiente.nombre] !== undefined}
              placeholder="Escribí parte del nombre para buscar…"
              onPick={(registro) => {
                setRegistroDelParam(registro.id);
                setNombreDelParam(registro.display_name);
                setFaltantes((actual) => {
                  if (actual[paramPendiente.nombre] === undefined) return actual;
                  const copia = { ...actual };
                  delete copia[paramPendiente.nombre];
                  return copia;
                });
              }}
            />
          ) : (
            <select
              id={`${id}-param`}
              className={controlClass(false, 'pr-8')}
              value={registroDelParam === null ? '' : String(registroDelParam)}
              onChange={(evento) => {
                setRegistroDelParam(evento.target.value === '' ? null : Number(evento.target.value));
              }}
            >
              <option value="">Elegí uno…</option>
              {candidatos.map((fila: RecordData) => (
                <option key={fila.id} value={String(fila.id)}>
                  {String(fila.display_name ?? fila.name ?? `#${fila.id}`)}
                </option>
              ))}
            </select>
          )}
          {(paramPendiente.ayuda ?? '') !== '' && (
            <p className="mt-1 text-xs text-fg-faint">{paramPendiente.ayuda}</p>
          )}
        </div>
      )}

      {/* Los datos que pide la ACCIÓN, con sus propios controles.
          Es el panel de la opción que da de alta el registro en vez de
          vincularlo: mismas etiquetas, mismas ayudas y mismos controles que el
          diálogo de la botonera, porque los declara la misma acción. Que estén
          acá adentro y no detrás de un botón es el pedido: se completan en el
          mismo formulario donde se dijo qué es el contacto. */}
      {necesitaAccion && hecho === null && paramsDeclarados.length > 0 && (
        <div className="rounded-md border border-dashed border-line bg-app-subtle px-3 py-3">
          <ParamsDeLaAccion
            params={paramsDeclarados}
            valorDe={leerParam}
            errorDe={(nombre) => faltantes[nombre] ?? null}
            onChange={(nombre, valor) => {
              setValoresDeLaAccion((actual) => ({ ...actual, [nombre]: valor }));
              setFaltantes((actual) => {
                if (actual[nombre] === undefined) return actual;
                const copia = { ...actual };
                delete copia[nombre];
                return copia;
              });
            }}
          />
        </div>
      )}

      {/* El CUIT ya no frena estas dos altas —una administracion prospecto entra
          sin él—, así que esto es un aviso y no una pared: dice qué va a hacer
          falta cuando se le facture. Por eso `role="status"` y no `alert`, y
          nunca `required` en el spec, que trabaría la edición de las fichas que
          no lo tienen. */}
      {necesitaAccion && faltaCuit && (
        <p role="status" className="text-xs font-medium text-warning">
          {opcionActual?.falta_cuit ?? 'Falta el CUIT.'}
        </p>
      )}

      {/* EL BOTÓN QUE ARREGLA EL DEFECTO 2, y por qué es un botón y no otra cosa.
          ================================================================
          Lo que pasaba: sobre un contacto YA GUARDADO, la acción quedaba anotada
          para «después del próximo guardado que salga bien» y el resto del
          camino se caía solo. Si faltaba completar un dato de la acción, el
          contacto se guardaba, el aviso pedía el dato… y completarlo NO ensucia
          el formulario —lo tipeado en estos controles es estado de este widget,
          no campos del contacto—, así que «Guardar» contestaba «No hay cambios
          para guardar» y la ficha no se creaba nunca. El sistema decía que sí y
          no hacía nada. (Y con volver a elegir la MISMA opción pasaba igual: un
          `<select>` no dispara `change` si el valor no cambió.)
          De los dos arreglos posibles se eligió éste —correr la acción EN EL
          MOMENTO— y no «hacer que elegir ensucie el formulario». El argumento:
          sobre un registro que ya existe no hay nada que guardar; los datos de
          la acción no son campos del contacto. Forzar el ensuciado obligaría a
          mandar un PATCH vacío sólo para destrabar la compuerta de «Guardar»,
          o sea escribir en la base para disparar un efecto colateral, y además
          dejaría a «Descartar» teniendo que deshacer estado que no es suyo.
          EN UN ALTA NO HAY BOTÓN Y SIGUE SIENDO `alGuardar`: ahí todavía no hay
          id, así que no hay sobre qué correr hasta que el guardado vuelva.
          Y la anotación SE MANTIENE también sobre un registro existente, además
          del botón: apretar «Guardar» es el reflejo natural, y si ese camino no
          hiciera nada estaríamos arreglando la mitad del defecto. Los dos
          caminos corren lo mismo y `hecho` impide que corran dos veces. */}
      {necesitaAccion && hecho === null && context.recordId !== null && (
        <button
          type="button"
          className="w-full rounded-md border border-accent bg-accent-soft px-3 py-2 text-sm font-medium text-fg transition-colors hover:border-accent-strong disabled:opacity-60"
          disabled={corriendo}
          onClick={() => {
            void ejecutar.current(context.recordId as number);
          }}
        >
          {corriendo ? 'Aplicándolo…' : yaEraOtraCosa ? 'Convertir este contacto' : 'Dar de alta la ficha'}
        </button>
      )}

      {/* Qué va a pasar, y CUÁNDO. Se lee antes, no después. La frase de un alta
          y la de una ficha existente son distintas porque el momento es
          distinto, y la vieja —«Se hace al guardar»— era, sobre un contacto ya
          guardado, literalmente falsa. */}
      {necesitaAccion && hecho === null && (
        <p className="text-xs text-fg-muted">
          {corriendo
            ? 'Dándolo de alta…'
            : context.recordId === null
              ? 'Se hace al guardar. Si algo falta, el contacto queda guardado igual y el aviso dice qué completar.'
              : yaEraOtraCosa
                ? 'Este contacto ya está guardado: esto no espera a «Guardar». Al apretar el botón se le retira la ficha que tiene y se le crea la nueva, o el sistema se niega diciendo qué la está reteniendo.'
                : 'Este contacto ya está guardado: esto no espera a «Guardar», se hace al apretar el botón.'}
        </p>
      )}

      {/* EL PANEL DE COMPLETAR. Mismos controles y mismas etiquetas que el alta,
          porque los declara la misma funcion (`_params_de_la_ficha`). Lo que
          cambia es que aca se ESCRIBE sobre la ficha que ya existe. */}
      {puedeCompletar && (accionCompletar?.params ?? []).length > 0 && (
        <div className="space-y-2 rounded-md border border-dashed border-line bg-app-subtle px-3 py-3">
          <p className="text-xs font-medium text-fg-muted">
            Sus datos{' '}
            {deducido === 'administracion'
              ? 'de administración'
              : deducido === 'consorcio'
                ? 'del consorcio'
                : 'de administrador'}
          </p>
          <ParamsDeLaAccion
            params={accionCompletar?.params ?? []}
            valorDe={(nombre) => valoresDeLaFicha[nombre] ?? null}
            errorDe={() => null}
            onChange={(nombre, valor) => {
              setValoresDeLaFicha((actual) => ({ ...actual, [nombre]: valor }));
            }}
          />
          <button
            type="button"
            className="w-full rounded-md border border-accent bg-accent-soft px-3 py-2 text-sm font-medium text-fg transition-colors hover:border-accent-strong disabled:opacity-60"
            disabled={guardandoFicha}
            onClick={() => {
              setGuardandoFicha(true);
              void api
                .callAction(
                  context.modelName,
                  context.recordId as number,
                  accionCompletar?.name ?? 'completar_ficha_del_vertical',
                  // Las relaciones prellenadas viajan como `{id, display_name}`
                  // para que el buscador las muestre; el servidor espera el id.
                  Object.fromEntries(
                    Object.entries(valoresDeLaFicha).map(([nombre, valor]) => [
                      nombre,
                      isMany2One(valor) ? valor.id : valor,
                    ]),
                  ),
                )
                .then((resultado) => {
                  notify.success('Sus datos', resultado.message ?? undefined);
                })
                .catch((error: unknown) => {
                  notify.error(
                    'No se pudieron guardar sus datos',
                    error instanceof Error ? error.message : undefined,
                  );
                })
                .finally(() => {
                  setGuardandoFicha(false);
                });
            }}
          >
            {guardandoFicha ? 'Guardando…' : 'Guardar sus datos'}
          </button>
          <p className="text-xs text-fg-faint">
            Lo que dejes vacío no se borra: se conserva lo que ya estaba. Esto no
            espera a «Guardar», se hace al apretar el botón.
          </p>
        </div>
      )}

      {/* Y qué pasó, cuando ya pasó. Es el parte del servidor tal cual: dice lo
          que se creó, lo que se retiró y lo que quedó afuera con su motivo. */}
      {hecho !== null && (
        <p role="status" className="text-xs font-medium text-success">
          {hecho}
        </p>
      )}
    </div>
  );
}
